import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectLanguage } from '../redux/Slices/busSlice';
import logo from '../../components/assets/logos/logo2.PNG';
import { FaHome } from 'react-icons/fa';

const ContactUs = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentLanguage = useSelector(selectLanguage);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    const htmlElement = document.documentElement;
    const bodyElement = document.body;
    htmlElement.lang = currentLanguage;
    htmlElement.classList.remove('hindi-lang');
    bodyElement.classList.remove('hindi-lang');
    if (currentLanguage === 'hi') {
      htmlElement.classList.add('hindi-lang');
      bodyElement.classList.add('hindi-lang');
    }
    htmlElement.classList.add('font-transition');
    bodyElement.classList.add('font-transition');
    return () => {
      htmlElement.classList.remove('hindi-lang', 'font-transition');
      bodyElement.classList.remove('hindi-lang', 'font-transition');
    };
  }, [currentLanguage]);

  const getFontConfig = () => {
    if (currentLanguage === 'hi') {
      return {
      fontFamily: "'Noto Serif Devanagari', serif",


        fontWeight: 500,
        className: 'hindi-lang font-hindi'
      };
    }
    return {
     fontFamily: "'Noto Serif Devanagari', serif",

      fontWeight: 400,
      className: 'font-english'
    };
  };

  const fontConfig = getFontConfig();

  const handleBack = () => navigate('/');

  const getAddress = () => {
    if (currentLanguage === 'hi') {
      return (
        <>
          74, कृष्णोदय नगर,<br />
          खण्डवा नाका, इन्दौर,<br />
          ज़िला - इन्दौर, मध्य प्रदेश,<br />
          भारत (पिन - 452001)
        </>
      );
    }
    return (
      <>
        74, Krishnodaya Nagar,<br />
        Khandwa Naka, Indore,<br />
        Dist. - Indore, Madhya Pradesh,<br />
        India (PIN - 452001)
      </>
    );
  };

  const businessHoursLabels = {
    title: currentLanguage === 'hi' ? 'व्यापार का समय' : 'Business Hours',
    hours: currentLanguage === 'hi' ? 'सोमवार - शनिवार: सुबह 9:00 - शाम 6:00' : 'Monday - Saturday: 9:00 AM - 6:00 PM',
    sunday: currentLanguage === 'hi' ? 'रविवार: बंद' : 'Sunday: Closed'
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      fontFamily: fontConfig.fontFamily,
      fontWeight: fontConfig.fontWeight,
      padding: isMobile ? '10px' : '0',
      overflowX: 'hidden',
      transition: 'font-family 0.3s ease, font-weight 0.3s ease'
    },
    header: {
      background: 'linear-gradient(135deg, #FFFDE7 0%, #FFF9C4 100%)',
      color: 'black',
      padding: isMobile ? '10px 20px' : '10px 40px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      borderBottom: '3px solid #ffeb3b'
    },
    headerContent: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    logoContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px'
    },
    logoLink: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      textDecoration: 'none',
      color: 'black'
    },
    logoImage: {
      height: isMobile ? '45px' : '60px',
      width: 'auto',
      objectFit: 'contain',
      borderRadius: '8px'
    },
    backButton: {
      background: 'none',
      border: 'none',
      color: 'black',
      fontSize: isMobile ? '1rem' : '1.1rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      padding: '8px 15px',
      borderRadius: '20px',
      backgroundColor: 'rgba(255, 255, 255, 0.3)',
      transition: 'all 0.3s ease'
    },
    mainContent: {
      flex: '1',
      padding: isMobile ? '20px' : '40px',
      maxWidth: '800px',
      margin: '0 auto',
      width: '100%',
      boxSizing: 'border-box'
    },
    pageCard: {
      background: 'white',
      borderRadius: '20px',
      padding: isMobile ? '30px' : '50px',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
      marginBottom: '30px'
    },
    pageTitle: {
      color: '#333',
      fontSize: isMobile ? '2rem' : '2.5rem',
      marginBottom: '40px',
      textAlign: 'center',
      fontWeight: 700,
      borderBottom: '3px solid #ffeb3b',
      paddingBottom: '15px'
    },
    contactInfoContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '30px',
      maxWidth: '600px',
      margin: '0 auto'
    },
    infoItem: {
      padding: '20px',
      background: '#f8f9fa',
      borderRadius: '15px',
      transition: 'all 0.3s ease',
      border: '2px solid transparent'
    },
    infoLabel: {
      color: '#333',
      fontSize: '1.1rem',
      fontWeight: 600,
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    infoValue: {
      color: '#666',
      fontSize: '1rem',
      lineHeight: '1.6',
      paddingLeft: '38px'
    },
    infoLink: {
      color: '#667eea',
      textDecoration: 'none',
      transition: 'color 0.3s ease'
    },
    businessHours: {
      padding: '25px',
      background: '#fff9c4',
      borderRadius: '15px',
      border: '2px solid #ffeb3b'
    },
    hoursTitle: {
      color: '#333',
      fontSize: '1.2rem',
      marginBottom: '12px',
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    hoursText: {
      color: '#555',
      fontSize: '1rem',
      lineHeight: '1.8',
      paddingLeft: '38px'
    },
    footer: {
      background: '#fff9c4',
      color: 'black',
      padding: isMobile ? '15px 20px' : '25px 40px',
      textAlign: 'center',
      marginTop: '40px',
      width: '100%',
      boxSizing: 'border-box'
    },
    footerText: {
      margin: '0 0 10px 0',
      opacity: '0.9',
      fontSize: isMobile ? '0.8rem' : '0.95rem'
    },
    footerLinks: {
      display: 'flex',
      justifyContent: 'center',
      flexWrap: 'wrap',
      gap: isMobile ? '15px' : '25px'
    },
    footerLink: {
      color: 'black',
      textDecoration: 'none',
      opacity: '0.8',
      transition: 'opacity 0.3s ease',
      fontSize: isMobile ? '0.8rem' : '0.9rem'
    }
  };

  return (
    <div style={styles.container} className={fontConfig.className} lang={currentLanguage}>
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.logoContainer}>
            <Link to="/" style={styles.logoLink}>
              <img src={logo} alt="Logo" style={styles.logoImage} />
            </Link>
          </div>
          <button 
            onClick={handleBack} 
            style={styles.backButton}
            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.5)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'}
          >
           <FaHome /> {t('home', 'Home')}
          </button>
        </div>
      </header>

      <main style={styles.mainContent}>
        <div style={styles.pageCard}>
          <h1 style={styles.pageTitle}>
            {t('contactUs', 'Contact Us')}
          </h1>

          <div style={styles.contactInfoContainer}>
            {/* Phone */}
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>
                <span style={{ fontSize: '1.3rem' }}>📞</span> {t('phone', 'Phone')}
              </div>
              <div style={styles.infoValue}>
                <a 
                  href="tel:9826722773" 
                  style={styles.infoLink}
                  onMouseEnter={(e) => e.target.style.color = '#764ba2'}
                  onMouseLeave={(e) => e.target.style.color = '#667eea'}
                >
                  9826722773
                </a>
              </div>
            </div>

            {/* Email */}
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>
                <span style={{ fontSize: '1.3rem' }}>✉️</span> {t('email', 'Email')}
              </div>
              <div style={styles.infoValue}>
                <a 
                  href="mailto:ikalorano@gmail.com" 
                  style={styles.infoLink}
                  onMouseEnter={(e) => e.target.style.color = '#764ba2'}
                  onMouseLeave={(e) => e.target.style.color = '#667eea'}
                >
                  ikalorano@gmail.com
                </a>
              </div>
            </div>

            {/* Address */}
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>
                <span style={{ fontSize: '1.3rem' }}>📍</span> {t('address', 'Address')}
              </div>
              <div style={styles.infoValue}>
                {getAddress()}
              </div>
            </div>

            {/* Business Hours */}
            <div style={styles.businessHours}>
              <div style={styles.hoursTitle}>
                <span style={{ fontSize: '1.3rem' }}>🕐</span> {businessHoursLabels.title}
              </div>
              <div style={styles.hoursText}>
                {businessHoursLabels.hours}<br />
                {businessHoursLabels.sunday}
              </div>
            </div>
          </div>
        </div>
      </main>

      <footer style={styles.footer}>
        <p style={styles.footerText}>
          © {new Date().getFullYear()} {t('companyName', 'Rajdoot Explorations')}. {t('allRightsReserved', 'All rights reserved.')}
        </p>
        <div style={styles.footerLinks}>
          <Link to="/privacy" style={styles.footerLink}>{t('privacyPolicy', 'Privacy Policy')}</Link>
          <Link to="/terms" style={styles.footerLink}>{t('termsOfService', 'Terms of Service')}</Link>
          <Link to="/contact" style={styles.footerLink}>{t('contactUs', 'Contact Us')}</Link>
        </div>
      </footer>
    </div>
  );
};

export default ContactUs;